<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs
			:breadcrumbs="page.breadcrumbs"
			:class="page.headingImage ? '' : 'no-slider'"
			:home="defaults[locale].website.hotelName"
		/>

		<main-content :title="page.title || page.header" :content="page.content" :booking-id="page.bookingID" />

		<section class="main-content background-color">
			<div class="row">
				<div v-for="review in reviews" :key="review.ID" class="columns column12">
					<div class="review">
						<h3>{{ review.header }}</h3>
						<span class="review-score">
							<font-awesome-icon
								v-for="(icon, index) in ratingToStars(review.rating)"
								:key="`${review.ID}-${icon}-${index}`"
								:icon="`fa ${icon}`"
								size="1x"
							/>
						</span>
						<p>"{{ review.content }}"</p>
						<div class="review-name">
							{{ review.firstName }}
							<span v-if="review.date">
								,
								{{ $d(new Date(review.date.date.split(' ')[0]), 'short') }}
							</span>
							<div class="review-logo" :class="review.source" />
						</div>
					</div>
				</div>
			</div>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const config = useRuntimeConfig();

await fetchPage();
await fetchDefaults();

const { data: reviews } = await useWebsiteFetch('reviews', {
	query: { language: locale.value },
	key: `${locale.value}/reviews`,
});

const getReviewTotalAverage = () => {
	const sum = reviews.value
		.filter((review) => review.rating)
		.reduce((total, current) => total + parseInt(current.rating, 10), 0);

	return sum / reviews.value.length;
};

const ratingToStars = (rating) => {
	const fullStars = parseInt(rating / 2, 10);
	const result = Array(fullStars).fill('fa-star');

	if ((rating / 2) % 1 !== 0) {
		result.push('fa-star-half');
	}

	return result;
};

const currentUrl = config.public.siteUrl;

useHead({
	script: [
		{
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@context': 'http://schema.org',
				'@type': 'AggregateRating',
				'@id': `${currentUrl}#aggregaterating`,
				bestRating: 10,
				ratingValue: getReviewTotalAverage(),
				ratingCount: reviews.value.length,
				itemReviewed: {
					'@id': `${currentUrl}#hotel`,
				},
			}),
		},
	],
});
</script>

<style lang="scss" scoped>
.review {
	background: #fff;
	padding: 50px;
	margin: 20px auto;
	box-shadow: 0 0 20px rgb(0 0 0 / 5%);

	h3 {
		margin: 0;
	}

	.review-score {
		color: #f67c01;
		margin: 15px 0;
		display: inline-block;
	}

	.review-name {
		display: flex;
		align-items: center;
		font-weight: 700;
		font-size: 12px;
	}

	.review-logo {
		display: inline-block;
		width: 75px;
		height: 50px;
		background-size: 75px 50px;
		margin: 5px 0 0 10px;

		&.tripadvisor {
			background-image: url('~/assets/images/review-logo-tripadvisor.png');
		}

		&.bookingcom {
			background-image: url('~/assets/images/review-logo-bookingcom.png');
		}

		&.zoover {
			background-image: url('~/assets/images/review-logo-zoover.png');
		}

		&.expedia {
			background-image: url('~/assets/images/review-logo-expedia.png');
		}

		&.google {
			background-image: url('~/assets/images/review-logo-google.png');
		}
	}
}
</style>
